import {
  createContext,
  useReducer,
  useEffect,
  useContext,
  useState,
} from "react";
import { useParams, useLocation } from "react-router-dom";
import { useGetSipTrunkByIdQuery } from "./api";
import { getDefaultTrunkGateway, DEFAULT_IP_BOX } from "./constants";
import { set, cloneDeep } from "lodash";
import { customerIdSelector } from "@/react/features/customer/customerSlice";
import { useSelector } from "react-redux";

const TrunkGatewayContext = createContext();

const ACTIONS = Object.freeze({
  INIT: "INIT",
  UPDATE_FIELD: "UPDATE_FIELD",
  ADD_IP_BOX: "ADD_IP_BOX",
  REMOVE_BOX: "REMOVE_BOX",
});

const trunkGatewayReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.INIT:
      return action.payload;
    case ACTIONS.UPDATE_FIELD: {
      const newState = cloneDeep(state);
      set(newState, action.payload.path, action.payload.value);
      return newState;
    }
    case ACTIONS.ADD_IP_BOX: {
      const { boxType } = action.payload;
      // Add box based on the provided type: IP Box (boxes) or Additional
      // Outbound IP Box (additionalBoxes)
      return {
        ...state,
        [boxType]: [...state[boxType], { ...DEFAULT_IP_BOX }],
      };
    }
    case ACTIONS.REMOVE_BOX: {
      const { boxType, index } = action.payload;
      // Remove box based on the provided type: IP Box (boxes) or Additional
      // Outbound IP Box (additionalBoxes)
      return {
        ...state,
        [boxType]: state[boxType].filter((_, i) => i !== index),
      };
    }
    default:
      return state;
  }
};

export const TrunkGatewayProvider = ({ children }) => {
  const { trunkId } = useParams();
  const location = useLocation();
  const isCreatePage = location.pathname.includes("/create");
  const customerId = useSelector(customerIdSelector);
  const { data: trunkGateway, isLoading } = useGetSipTrunkByIdQuery(
    { trunkId },
    {
      skip: isCreatePage,
    }
  );

  const [state, dispatch] = useReducer(trunkGatewayReducer, {});
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (isCreatePage) {
      dispatch({
        type: ACTIONS.INIT,
        payload: getDefaultTrunkGateway(customerId),
      });
    } else if (trunkGateway && !isCreatePage) {
      dispatch({ type: ACTIONS.INIT, payload: trunkGateway });
    }
  }, [trunkGateway, isCreatePage]);

  const resetTrunkGateway = (trunkGateway = null) => {
    dispatch({
      type: ACTIONS.INIT,
      payload: trunkGateway ?? getDefaultTrunkGateway(customerId),
    });
  };

  const updateField = (path, value) => {
    dispatch({ type: ACTIONS.UPDATE_FIELD, payload: { path, value } });
  };

  const addIpBox = ({ boxType }) => {
    dispatch({ type: ACTIONS.ADD_IP_BOX, payload: { boxType } });
  };

  const removeIpBox = ({ boxType, index }) => {
    dispatch({ type: ACTIONS.REMOVE_BOX, payload: { boxType, index } });
  };

  return (
    <TrunkGatewayContext.Provider
      value={{
        state,
        fetchedTrunkGateway: trunkGateway,
        resetTrunkGateway,
        updateField,
        addIpBox,
        removeIpBox,
        isValid,
        setIsValid,
        isLoading,
      }}
    >
      {children}
    </TrunkGatewayContext.Provider>
  );
};

export const useTrunkGatewayContext = () => {
  return useContext(TrunkGatewayContext);
};
